import ReactGA from 'react-ga4'

import {
  leadMagnetPageFetch,
  leadMagnetPagePlayVideo,
  leadMagnetPageClearVideo,
  claimLeadMagnet,
  tagInterest,
  disableCtaAction,
  enableCtaAction,
  types,
  hideDialogAction,
  dialogSubmittedAction,
  choosePathAction
} from './'
import notifications, {
  toastError,
  toastServerError,
  toastSuccess
} from '../../../global/notifications'
import { getCookie, setCookie, deleteCookie } from '../utils/cookie'
import { testimonialsFetch } from '../testimonials'

// The chosen path survives a refresh so returning visitors land on their
// lessons, not the question again. sessionStorage can throw (private mode,
// blocked site data) — the page must keep working without it.
const pathStorageKey = (pageSlug) => `lm-path-${pageSlug}`

export const readStoredPath = (pageSlug) => {
  try {
    return window.sessionStorage.getItem(pathStorageKey(pageSlug))
  } catch (_) {
    return null
  }
}

// Opaque lead identity from the capture endpoint (expires server-side after
// 30 days). The standalone landing site on pages.stanwinstonschool.com
// (SPEC-052 static-site revision) writes it as a parent-domain cookie —
// localStorage is per-origin, so a token written there would be invisible
// to this app on www. Cookie first, localStorage fallback: cross-repo
// deploy order doesn't matter and in-flight localStorage tokens keep
// working. Same parent-domain pattern as SPEC-010's CloudFront cookies.
const LEAD_TOKEN_KEY = 'lmLeadToken'
export const LEAD_TOKEN_COOKIE = 'swsLeadToken'
const LEAD_TOKEN_MAX_AGE = 30 * 24 * 60 * 60 * 1000

// Domain=.stanwinstonschool.com only where the browser will accept it
// (www, local.* dev); elsewhere (tests, previews) a host-only cookie
const cookieOptions = () => {
  const options = { path: '/', samesite: 'Lax', maxage: LEAD_TOKEN_MAX_AGE }
  try {
    if (window.location.hostname.endsWith('stanwinstonschool.com'))
      options.domain = '.stanwinstonschool.com'
    if (window.location.protocol === 'https:') options.secure = true
  } catch (_) {
    // defaults are fine
  }
  return options
}

export const readLeadToken = () => {
  try {
    const fromCookie = getCookie(LEAD_TOKEN_COOKIE)
    if (fromCookie) return fromCookie
  } catch (_) {
    // fall through to localStorage
  }
  try {
    return window.localStorage.getItem(LEAD_TOKEN_KEY)
  } catch (_) {
    return null
  }
}

export const storeLeadToken = (token) => {
  if (!token) return
  try {
    setCookie(LEAD_TOKEN_COOKIE, token, cookieOptions())
  } catch (_) {
    // cookie blocked: localStorage still covers same-origin flows
  }
  try {
    window.localStorage.setItem(LEAD_TOKEN_KEY, token)
  } catch (_) {
    // tagging quietly degrades to "no identity"
  }
}

export const clearLeadToken = () => {
  try {
    deleteCookie(LEAD_TOKEN_COOKIE, cookieOptions())
  } catch (_) {
    // nothing to clear
  }
  try {
    window.localStorage.removeItem(LEAD_TOKEN_KEY)
  } catch (_) {
    // nothing to clear
  }
}

export const leadMagnetPageDispatchers = (dispatch, props) => ({
  loadLeadMagnetPage() {
    dispatch(leadMagnetPageFetch(props.params.slug)())
  },
  loadLeadMagnetPageVideo(payload) {
    dispatch(leadMagnetPagePlayVideo(payload))
  },
  // SPEC-053 FR-6: real proof for the trial block's fallback tier
  loadTestimonials() {
    dispatch(testimonialsFetch())
  },
  // SPEC-053 Q4: a locked-chapter click is the clearest buying signal on
  // the page — record which chapter pulled it before the anchor navigates
  trackLockedChapter(chapter) {
    try {
      ReactGA.event({
        category: 'LeadGen',
        action: 'locked_chapter_click',
        label: `${props.params.slug} · lesson ${chapter.number} · ${chapter.title}`
      })
    } catch (_) {
      // analytics must never block the navigation
    }
  },
  clearLeadMagnetPageVideo() {
    dispatch(leadMagnetPageClearVideo())
  },
  // SPEC-052 FR-11. The choice always resequences the page; Mailchimp
  // interest tagging happens silently and only when a stored lead_token
  // identifies the lead (Phase 2 capture) — never by prompting for an email
  // here, which would reintroduce the duplicate ask FR-7 removes.
  choosePath(pathSlug, interestTag) {
    dispatch(choosePathAction(pathSlug))
    try {
      window.sessionStorage.setItem(pathStorageKey(props.params.slug), pathSlug)
    } catch (_) {
      // per-visitor convenience only; losing it just re-asks the question
    }
    const leadToken = readLeadToken()
    if (!interestTag || !leadToken) return
    dispatch(tagInterest({ tag: interestTag, leadToken }))
      .then(([, res]) => {
        if (res.type === types.LEAD_MAGNET_INTEREST_TAG_SUCCESS)
          storeLeadToken(res.payload?.leadToken)
        // invalid/expired token: forget it so we stop sending it
        else if (res.payload?.leadToken) clearLeadToken()
      })
      .catch(() => {
        // silent by design — the page already switched to the chosen path
      })
  },
  // Restoring after a refresh resequences the page but never re-fires the
  // interest call: it goes out once per actual choice.
  restoreStoredPath() {
    const stored = readStoredPath(props.params.slug)
    if (stored) dispatch(choosePathAction(stored))
  }
})

// SPEC-091 FR-8: bump when the www consent copy changes; backend maps the
// version to the exact sentence it stores in Mailchimp (CONSENTTXT)
export const WWW_CONSENT_TEXT_VERSION = '2026-10-www'

// Every signup form on the site joins the same Mailchimp welcome series
// (Nate, 2026-10): one tag for all of them, whatever page they sit on.
export const WEBSITE_LEAD_TAG = 'Lead - 16 Lessons - Website'

export const onLeadMagnetFunnelFormDispatch = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    onSubmit: (data) => {
      // the button stays disabled until the box is ticked; never capture
      // an email without the consent it records
      if (!data.consent) return
      dispatch(disableCtaAction())
      dispatch(
        claimLeadMagnet({
          email: data.email,
          tag: WEBSITE_LEAD_TAG,
          // which form it came from, stored on the Lead (not in Mailchimp)
          surface: ownProps.surface,
          consent: true,
          consentTextVersion: WWW_CONSENT_TEXT_VERSION
        })
      )
        .then(([_, res]) => {
          if (res.type === types.CLAIM_LEAD_MAGNET_REQUEST_SUCCESS) {
            // SPEC-052: capture now returns an opaque lead_token; keeping it
            // lets the path chooser tag this lead's interests later
            storeLeadToken(res.payload?.leadToken)
            toastSuccess(notifications.funnelConversion)
            dispatch(hideDialogAction())
            dispatch(dialogSubmittedAction())
          }
          if (res.type === types.CLAIM_LEAD_MAGNET_REQUEST_FAILURE) {
            toastError(notifications.funnelConversion)
            if (res?.payload?.email[0])
              toastServerError(res?.payload?.email[0])
          }
        })
        .catch((payload) => {
          const error = payload.errors
          toastServerError(error)
        })
        .finally(() => dispatch(enableCtaAction()))
    }
  }
}
